import { motion } from "framer-motion";
import { LucideIcon, ArrowRight } from "lucide-react";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";

export interface ServiceItem {
  name: string;
  icon: LucideIcon;
  description?: string;
  components: string[];
  outcome: string;
}

interface ServicesGridProps {
  label?: string;
  title: string;
  description?: string;
  services: ServiceItem[];
}

const ServicesGrid = ({ 
  label = "The System", 
  title, 
  description,
  services 
}: ServicesGridProps) => {
  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border">
      <div className="container-wide">
        {/* Section Header */}
        <motion.div 
          className="max-w-3xl mb-8 md:mb-12 lg:mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.span 
            variants={sectionVariant}
            className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block"
          >
            {label}
          </motion.span>
          <motion.h2 
            variants={sectionVariant}
            className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground"
          >
            {title}
          </motion.h2>
          {description && (
            <motion.p 
              variants={sectionVariant}
              className="text-muted-foreground text-base md:text-lg max-w-2xl mt-4"
            >
              {description}
            </motion.p>
          )}
        </motion.div>

        {/* Services Cards Grid - 2 columns on desktop */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-6 md:gap-y-10 lg:gap-y-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <motion.div 
                key={service.name}
                variants={itemVariant}
                className="flex gap-5"
              >
                {/* Circular Icon */}
                <div className="w-12 h-12 rounded-full border border-border bg-background flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  {/* Title */}
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    {service.name}
                  </h3>
                  
                  {/* Description */}
                  {service.description && (
                    <p className="text-muted-foreground text-sm mb-4">
                      {service.description}
                    </p>
                  )}
                  
                  {/* Bullet Items with dots */}
                  <ul className="space-y-2 mb-5">
                    {service.components.map((component) => (
                      <li 
                        key={component} 
                        className="text-sm text-muted-foreground flex items-start gap-2"
                      >
                        <span className="text-muted-foreground/60 mt-1.5 leading-none">·</span>
                        {component}
                      </li>
                    ))}
                  </ul>
                  
                  {/* Outcome */}
                  <p className="text-sm text-primary flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{service.outcome}</span>
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid;
